import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    ArrowForward as BackIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useFetch from '../../../../../hooks/useFetch';
import useDeleteItem from '../../../../../hooks/useDeleteItem';
import RTL from '../../../../../utils/RTL';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';
import StandardTable from '../../../../common/table/StandardTable';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import BankReconciliationForm from './BankReconciliationForm';
import api from '../../../../../config/axiosConfig';

function BankReconciliation() {
    const theme = useTheme();
    const [filters, setFilters] = useState({
        is_matched: '',
        start_date: '',
        end_date: '',
    });
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [matchDialogOpen, setMatchDialogOpen] = useState(false);
    const [selectedReconciliation, setSelectedReconciliation] = useState(null);
    const [reconciliationToDelete, setReconciliationToDelete] = useState(null);
    const [reconciliationToMatch, setReconciliationToMatch] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [refreshToken, setRefreshToken] = useState(0);

    const { deleteItem, isLoading: isDeleting } = useDeleteItem();

    // Build API params
    const apiParams = useMemo(() => {
        const params = {};
        if (filters.is_matched !== '') params.is_matched = filters.is_matched === 'true' ? 'true' : 'false';
        if (filters.start_date) {
            const dateStr = filters.start_date instanceof Date 
                ? filters.start_date.toISOString().split('T')[0] 
                : filters.start_date;
            params.start_date = dateStr;
        }
        if (filters.end_date) {
            const dateStr = filters.end_date instanceof Date 
                ? filters.end_date.toISOString().split('T')[0] 
                : filters.end_date;
            params.end_date = dateStr;
        }
        return params;
    }, [filters]);

    const { data, loading, error, refetch } = useFetch(API.bankReconciliations, { 
        params: apiParams,
    });

    const reconciliations = data?.results || data || [];
    const totalCount = data?.count || reconciliations.length;
    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearFilters = () => {
        setFilters({
            is_matched: '',
            start_date: '',
            end_date: '',
        });
    };

    const handleCreate = () => {
        setSelectedReconciliation(null);
        setFormDialogOpen(true);
    };

    const handleEdit = (reconciliation) => {
        setSelectedReconciliation(reconciliation);
        setFormDialogOpen(true);
    };

    const handleDelete = (reconciliation) => {
        setReconciliationToDelete(reconciliation);
        setDeleteDialogOpen(true);
    };

    const handleMatch = (reconciliation) => {
        setReconciliationToMatch(reconciliation);
        setMatchDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!reconciliationToDelete) return;
        try {
            await deleteItem(`${API.bankReconciliations}${reconciliationToDelete.id}/`);
            setSnackbar({ 
                open: true, 
                message: 'تطبیق بانکی با موفقیت حذف شد.', 
                severity: 'success' 
            });
            refetch();
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err?.response?.data?.detail || 'خطا در حذف تطبیق بانکی', 
                severity: 'error' 
            });
        } finally {
            setDeleteDialogOpen(false);
            setReconciliationToDelete(null);
        }
    };

    const confirmMatch = async () => {
        if (!reconciliationToMatch) return;
        try {
            await api.post(`${API.bankReconciliations}${reconciliationToMatch.id}/match/`);
            setSnackbar({ 
                open: true, 
                message: 'تطبیق بانکی با موفقیت بررسی شد.', 
                severity: 'success' 
            });
            refetch();
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err?.response?.data?.detail || 'خطا در بررسی تطبیق بانکی', 
                severity: 'error' 
            });
        } finally {
            setMatchDialogOpen(false);
            setReconciliationToMatch(null);
        }
    };

    const handleFormSuccess = () => {
        setFormDialogOpen(false);
        setSelectedReconciliation(null);
        refetch();
        setRefreshToken(prev => prev + 1);
        setSnackbar({ 
            open: true, 
            message: selectedReconciliation ? 'تطبیق بانکی با موفقیت ویرایش شد.' : 'تطبیق بانکی با موفقیت ثبت شد.', 
            severity: 'success' 
        });
    };

    const formatCurrency = (amount) => {
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleDateString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    const columns = [
        {
            id: 'operation_details',
            label: 'عملیات',
            align: 'start',
            render: (reconciliation) => {
                const opDetails = reconciliation.operation_details || {};
                // The operation_details from serializer contains: id, amount, payment_method, payment_number, date_belonged
                // We need to fetch operation details separately or display what we have
                return (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            عملیات #{opDetails.id || reconciliation.operation || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatCurrency(opDetails.amount || 0)} تومان
                            {opDetails.date_belonged && ` - ${formatDate(opDetails.date_belonged)}`}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            id: 'bank_statement_date',
            label: 'تاریخ\nصورتحساب',
            render: (reconciliation) => (
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {formatDate(reconciliation.bank_statement_date)}
                </Typography>
            )
        },
        {
            id: 'bank_reference',
            label: 'شماره\nمرجع',
            priority: 'secondary',
            render: (reconciliation) => (
                <Typography variant="body2">
                    {reconciliation.bank_reference || '-'}
                </Typography>
            )
        },
        {
            id: 'operation_payment_method',
            label: 'روش\nپرداخت',
            priority: 'secondary',
            render: (reconciliation) => {
                const opDetails = reconciliation.operation_details || {};
                const paymentMethod = opDetails.payment_method || '-';
                return (
                    <Typography variant="body2">
                        {paymentMethod}
                    </Typography>
                );
            }
        },
        {
            id: 'operation_payment_number',
            label: 'شماره\nپرداخت',
            priority: 'secondary',
            render: (reconciliation) => {
                const opDetails = reconciliation.operation_details || {};
                return (
                    <Typography variant="body2">
                        {opDetails.payment_number || '-'}
                    </Typography>
                );
            }
        },
        {
            id: 'is_matched',
            label: 'وضعیت',
            compact: true,
            render: (reconciliation) => (
                <Chip
                    label={reconciliation.is_matched ? 'مطابقت یافته' : 'نیازمند بررسی'}
                    size="small"
                    color={reconciliation.is_matched ? 'success' : 'warning'}
                    icon={!reconciliation.is_matched ? <WarningIcon /> : undefined}
                />
            )
        },
        {
            id: 'verifier_name',
            label: 'بررسی‌کننده',
            align: 'start',
            priority: 'secondary',
            render: (reconciliation) => (
                <Box>
                    <Typography variant="body2">
                        {reconciliation.verifier_name || '-'}
                    </Typography>
                    {reconciliation.verified_at && (
                        <Typography variant="caption" color="text.secondary">
                            {formatDateTime(reconciliation.verified_at)}
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            id: 'actions',
            label: 'عملیات',
            compact: true,
            render: (reconciliation) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    {!reconciliation.is_matched && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleMatch(reconciliation);
                            }}
                            color="success"
                            aria-label="مطابقت"
                        >
                            <CheckCircleIcon fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(reconciliation);
                        }}
                        color="primary"
                        aria-label="ویرایش"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(reconciliation);
                        }}
                        color="error"
                        aria-label="حذف"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )
        },
    ];

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                {formDialogOpen ? (
                    /* Full-page create/edit form (drill-down) */
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                            <Button
                                startIcon={<BackIcon />}
                                variant="outlined"
                                onClick={() => {
                                    setFormDialogOpen(false);
                                    setSelectedReconciliation(null);
                                }}
                            >
                                بازگشت به فهرست
                            </Button>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {selectedReconciliation ? 'ویرایش تطبیق بانکی' : 'ثبت تطبیق بانکی جدید'}
                            </Typography>
                        </Box>
                        <Paper sx={{ p: 3 }}>
                            <BankReconciliationForm
                                reconciliation={selectedReconciliation}
                                onSuccess={handleFormSuccess}
                                onCancel={() => {
                                    setFormDialogOpen(false);
                                    setSelectedReconciliation(null);
                                }}
                            />
                        </Paper>
                    </Box>
                ) : (
                <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        تطبیق بانکی
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                        sx={{ mr: 'auto' }}
                    >
                        ثبت تطبیق جدید
                    </Button>
                </Box>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FilterListIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">فیلترها</Typography>
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                sx={{ mr: 'auto' }}
                            >
                                پاک کردن فیلترها
                            </Button>
                        )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid 
                        container 
                        spacing={2}
                        sx={{
                            '& .MuiTextField-root': { m: 0 },
                            '& .MuiFormControl-root': { m: 0 },
                        }}
                    >
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>وضعیت مطابقت</InputLabel>
                                <Select
                                    value={filters.is_matched}
                                    label="وضعیت مطابقت"
                                    onChange={(e) => handleFilterChange('is_matched', e.target.value || '')}
                                >
                                    <MenuItem value="">همه</MenuItem>
                                    <MenuItem value="true">مطابقت یافته</MenuItem>
                                    <MenuItem value="false">نیازمند بررسی</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <PersianDatePicker
                                label="از تاریخ"
                                value={filters.start_date ? new Date(filters.start_date) : null}
                                onChange={(date) => {
                                    if (date && !isNaN(date.getTime())) {
                                        handleFilterChange('start_date', date.toISOString().split('T')[0]);
                                    } else {
                                        handleFilterChange('start_date', '');
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <PersianDatePicker
                                label="تا تاریخ"
                                value={filters.end_date ? new Date(filters.end_date) : null}
                                onChange={(date) => {
                                    if (date && !isNaN(date.getTime())) {
                                        handleFilterChange('end_date', date.toISOString().split('T')[0]);
                                    } else {
                                        handleFilterChange('end_date', '');
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Unmatched Warning */}
                {!loading && reconciliations.filter(r => !r.is_matched).length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {toPersianDigits(reconciliations.filter(r => !r.is_matched).length)} تطبیق نیازمند بررسی است.
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Results Count */}
                {!loading && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            تعداد نتایج: {toPersianDigits(totalCount)}
                        </Typography>
                    </Box>
                )}

                {/* Reconciliations Table */}
                {!loading && (
                    <StandardTable
                        columns={columns}
                        rows={reconciliations}
                        loading={false}
                        error={error}
                        emptyText="تطبیق بانکی یافت نشد."
                        ariaLabel="جدول تطبیق‌های بانکی"
                        onRowClick={handleEdit}
                    />
                )}
                </>
                )}

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setReconciliationToDelete(null);
                    }}
                >
                    <DialogTitle>تأیید حذف</DialogTitle>
                    <DialogContent>
                        <Typography>
                            آیا از حذف این تطبیق بانکی اطمینان دارید؟ این عمل قابل بازگشت نیست.
                        </Typography>
                        {reconciliationToDelete && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    تاریخ صورتحساب: {formatDate(reconciliationToDelete.bank_statement_date)}
                                </Typography>
                                {reconciliationToDelete.bank_reference && (
                                    <Typography variant="body2" color="text.secondary">
                                        شماره مرجع: {reconciliationToDelete.bank_reference}
                                    </Typography>
                                )}
                                {reconciliationToDelete.operation_details && (
                                    <Typography variant="body2" color="text.secondary">
                                        عملیات: #{reconciliationToDelete.operation_details.id || reconciliationToDelete.operation || '-'}
                                        {' - '}{formatCurrency(reconciliationToDelete.operation_details.amount || 0)} تومان
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => {
                                setDeleteDialogOpen(false);
                                setReconciliationToDelete(null);
                            }}
                        >
                            انصراف
                        </Button>
                        <Button 
                            onClick={confirmDelete} 
                            color="error" 
                            variant="contained"
                            disabled={isDeleting}
                        >
                            حذف
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Match Confirmation Dialog */}
                <Dialog
                    open={matchDialogOpen}
                    onClose={() => {
                        setMatchDialogOpen(false);
                        setReconciliationToMatch(null);
                    }}
                >
                    <DialogTitle>مطابقت تطبیق بانکی</DialogTitle>
                    <DialogContent>
                        <Typography>
                            آیا از مطابقت این تطبیق بانکی اطمینان دارید؟
                        </Typography>
                        {reconciliationToMatch && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    تاریخ صورتحساب: {formatDate(reconciliationToMatch.bank_statement_date)}
                                </Typography>
                                {reconciliationToMatch.bank_reference && (
                                    <Typography variant="body2" color="text.secondary">
                                        شماره مرجع: {reconciliationToMatch.bank_reference}
                                    </Typography>
                                )}
                                {reconciliationToMatch.operation_details && (
                                    <>
                                        <Typography variant="body2" color="text.secondary">
                                            عملیات: #{reconciliationToMatch.operation_details.id || reconciliationToMatch.operation || '-'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            مبلغ: {formatCurrency(reconciliationToMatch.operation_details.amount || 0)} تومان
                                        </Typography>
                                        {reconciliationToMatch.operation_details.payment_method && (
                                            <Typography variant="body2" color="text.secondary">
                                                روش پرداخت: {reconciliationToMatch.operation_details.payment_method}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => {
                                setMatchDialogOpen(false);
                                setReconciliationToMatch(null);
                            }}
                        >
                            انصراف
                        </Button>
                        <Button 
                            onClick={confirmMatch} 
                            color="success" 
                            variant="contained"
                        >
                            مطابقت
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={() => setSnackbar({ ...snackbar, open: false })} 
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </RTL>
    );
}

export default BankReconciliation;

